import { useCallback, useEffect, useRef, useState } from "react";
import {
  AccountRequestError,
  type TeamSkillHistoryResult,
  type TeamSkillLibraryItem,
  type TeamSkillOwnerAction,
  type TeamSkillVersionComparisonResult,
  type TeamSkillsLibraryResult,
  type WorkspaceRole,
} from "../../lib/account-client";
import { FolderPicker } from "../upload/FolderPicker";
import { TeamSkillHistory } from "./TeamSkillHistory";

const PENDING_OWNER_ACTION_KEY = "doorway.team-skill.pending-owner-action";
const OWNER_STEP_UP_KEY = "doorway.owner-step-up";
const DOORWAY_SYNC_DOWNLOAD_URL = "https://updates.meetdoorway.com/Doorway-Sync.dmg";

export interface TeamSkillsLibraryApi {
  getTeamSkillsLibrary(workspaceId: string): Promise<TeamSkillsLibraryResult>;
  getTeamSkillHistory(workspaceId: string, skillId: string, cursors?: { versionCursor?: number; eventCursor?: number }): Promise<TeamSkillHistoryResult>;
  getTeamSkillVersionComparison(workspaceId: string, skillId: string, versionId: string, baseVersionId?: string): Promise<TeamSkillVersionComparisonResult>;
  publishTeamSkillVersion(workspaceId: string, skillId: string, versionId: string, generation: number): Promise<unknown>;
  changeTeamSkillState(workspaceId: string, skillId: string, action: "lock" | "unlock" | "disable", generation: number): Promise<unknown>;
  beginTeamSkillOwnerStepUp(workspaceId: string, skillId: string, input: { action: TeamSkillOwnerAction; approvalId?: string; versionId?: string; expectedStateGeneration: number }): Promise<{ authorizationUrl: string; binding: { workspaceId: string; skillId: string; action: TeamSkillOwnerAction; versionId: string | null; expectedStateGeneration: number } }>;
  completeTeamSkillOwnerAction(workspaceId: string, skillId: string, input: { action: TeamSkillOwnerAction; approvalId?: string; versionId?: string; expectedStateGeneration: number; capability: string }): Promise<unknown>;
  replaceTeamSkillTags(workspaceId: string, skillId: string, tags: string[]): Promise<{ tags: string[] }>;
  uploadTeamSkill(workspaceId: string, files: File[]): Promise<{
    skillId: string;
    versionId: string;
    displayName: string;
  }>;
}

interface Confirmation {
  action: TeamSkillOwnerAction;
  skill: TeamSkillLibraryItem;
  versionId?: string;
  binding: string;
}

export function TeamSkillsLibrary({ workspaceId, role, api }: { workspaceId: string; role: WorkspaceRole; api: TeamSkillsLibraryApi }) {
  const [library, setLibrary] = useState<TeamSkillsLibraryResult | null>(null);
  const [history, setHistory] = useState<TeamSkillHistoryResult | null>(null);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagSearch, setTagSearch] = useState("");
  const [newTag, setNewTag] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [confirmationText, setConfirmationText] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const confirmationInput = useRef<HTMLInputElement>(null);
  const confirmationDialog = useRef<HTMLElement>(null);
  const uploadButton = useRef<HTMLButtonElement>(null);
  const uploadDialog = useRef<HTMLElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const historyRequestGeneration = useRef(0);
  const selectedSkillIdRef = useRef<string | null>(null);
  const apiRef = useRef(api);
  apiRef.current = api;

  const load = useCallback(async (preferredSkillId?: string | null) => {
    const requestGeneration = ++historyRequestGeneration.current;
    setError(null);
    const next = await apiRef.current.getTeamSkillsLibrary(workspaceId);
    if (requestGeneration !== historyRequestGeneration.current || next.workspaceId !== workspaceId) return;
    setLibrary(next);
    const skillId = preferredSkillId ?? selectedSkillIdRef.current ?? next.skills[0]?.skillId ?? null;
    selectedSkillIdRef.current = skillId;
    setSelectedSkillId(skillId);
    if (!skillId) {
      setHistory(null);
      return;
    }
    const nextHistory = await apiRef.current.getTeamSkillHistory(workspaceId, skillId);
    if (requestGeneration !== historyRequestGeneration.current
      || selectedSkillIdRef.current !== skillId
      || nextHistory.workspaceId !== workspaceId
      || nextHistory.skill.skillId !== skillId) return;
    setHistory(nextHistory);
  }, [workspaceId]);

  useEffect(() => {
    selectedSkillIdRef.current = null;
    void load().catch((cause: unknown) => setError(message(cause, "Doorway could not load Team Skills.")));
    return () => { historyRequestGeneration.current += 1; };
  }, [load]);

  useEffect(() => {
    if (!confirmation) return;
    confirmationInput.current?.focus();
  }, [confirmation]);

  useEffect(() => {
    if (uploadOpen) uploadDialog.current?.focus();
  }, [uploadOpen]);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const verified = readStoredRecord(OWNER_STEP_UP_KEY);
    const requested = readStoredRecord(PENDING_OWNER_ACTION_KEY);
    if (!verified || !requested) return;
    sessionStorage.removeItem(OWNER_STEP_UP_KEY);
    sessionStorage.removeItem(PENDING_OWNER_ACTION_KEY);
    const binding = verified.binding as Record<string, unknown> | undefined;
    if (binding?.workspaceId !== workspaceId || binding.skillId !== requested.skillId
      || binding.action !== requested.action || binding.versionId !== (requested.bindingVersionId ?? null)
      || binding.expectedStateGeneration !== requested.expectedStateGeneration
      || typeof verified.capability !== "string") {
      setError("Owner verification did not match this exact skill change. Please try again.");
      return;
    }
    setPending(true);
    void apiRef.current.completeTeamSkillOwnerAction(workspaceId, String(requested.skillId), {
      action: requested.action as TeamSkillOwnerAction,
      ...(typeof requested.approvalId === "string" ? { approvalId: requested.approvalId } : {}),
      ...(typeof requested.versionId === "string" ? { versionId: requested.versionId } : {}),
      expectedStateGeneration: Number(requested.expectedStateGeneration), capability: verified.capability,
    }).then(async () => {
      setNotice("The verified Team Skills change is complete.");
      await load(String(requested.skillId));
    }).catch(handleMutationError).finally(() => setPending(false));
  }, [load, workspaceId]);

  const handleMutationError = useCallback((cause: unknown) => {
    if (cause instanceof AccountRequestError && cause.status === 409) {
      setNotice("Team Live changed while you were working. Doorway refreshed the latest version.");
      void load().catch(() => setError("Doorway could not refresh Team Skills."));
      return;
    }
    setError(message(cause, "Doorway could not complete that Team Skills change."));
  }, [load]);

  const selectSkill = async (skillId: string) => {
    const requestGeneration = ++historyRequestGeneration.current;
    selectedSkillIdRef.current = skillId;
    setSelectedSkillId(skillId);
    setHistory(null);
    setError(null);
    try {
      const nextHistory = await api.getTeamSkillHistory(workspaceId, skillId);
      if (requestGeneration !== historyRequestGeneration.current
        || selectedSkillIdRef.current !== skillId
        || nextHistory.workspaceId !== workspaceId
        || nextHistory.skill.skillId !== skillId) return;
      setHistory(nextHistory);
    } catch (cause) {
      if (requestGeneration === historyRequestGeneration.current && selectedSkillIdRef.current === skillId) {
        setError(message(cause, "Doorway could not load that skill."));
      }
    }
  };

  const publish = async (versionId: string) => {
    if (!history) return;
    setPending(true); setError(null);
    try {
      await api.publishTeamSkillVersion(workspaceId, history.skill.skillId, versionId, history.skill.stateGeneration);
      setNotice("The version is now Team Live.");
      await load(history.skill.skillId);
    } catch (cause) { handleMutationError(cause); }
    finally { setPending(false); }
  };

  const loadMoreHistory = async () => {
    if (!history || loadingMoreHistory) return;
    const skillId = history.skill.skillId;
    setLoadingMoreHistory(true);
    setError(null);
    try {
      const next = await api.getTeamSkillHistory(workspaceId, skillId, {
        versionCursor: history.pagination.versions.nextCursor ?? 0,
        eventCursor: history.pagination.events.nextCursor ?? 0,
      });
      if (selectedSkillIdRef.current !== skillId || next.skill.skillId !== skillId) return;
      setHistory((current) => current?.skill.skillId === skillId ? {
        ...next,
        versions: [...current.versions, ...next.versions.filter((version) =>
          !current.versions.some((existing) => existing.versionId === version.versionId))],
        events: [...current.events, ...next.events.filter((event) =>
          !current.events.some((existing) => existing.releaseEventId === event.releaseEventId))],
      } : current);
    } catch (cause) {
      setError(message(cause, "Doorway could not load more skill history."));
    } finally {
      setLoadingMoreHistory(false);
    }
  };

  const directOwnerAction = async (action: "lock" | "unlock" | "disable") => {
    if (!history) return;
    setPending(true); setError(null);
    try {
      await api.changeTeamSkillState(workspaceId, history.skill.skillId, action, history.skill.stateGeneration);
      setNotice(action === "disable" ? "The skill is disabled and downloads have stopped." : `The skill is ${action === "lock" ? "locked" : "unlocked"}.`);
      await load(history.skill.skillId);
    } catch (cause) { handleMutationError(cause); }
    finally { setPending(false); }
  };

  const saveTags = async (skill: TeamSkillLibraryItem, tags: string[]) => {
    setPending(true);
    setError(null);
    try {
      await api.replaceTeamSkillTags(workspaceId, skill.skillId, tags);
      setNewTag("");
      setNotice("Tags updated.");
      await load(skill.skillId);
    } catch (cause) {
      handleMutationError(cause);
    } finally {
      setPending(false);
    }
  };

  const shareUploadedSkill = async () => {
    if (uploadFiles.length === 0 || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const result = await api.uploadTeamSkill(workspaceId, uploadFiles);
      setNotice(`${result.displayName} is now shared with your team.`);
      setUploadOpen(false);
      setUploadFiles([]);
      queueMicrotask(() => uploadButton.current?.focus());
      try {
        await load(result.skillId);
      } catch (cause) {
        setError(message(cause, "The skill is shared, but Doorway could not refresh the library."));
      }
    } catch (cause) {
      setUploadError(uploadMessage(cause));
    } finally {
      setUploading(false);
    }
  };

  const requestOwnerAction = (action: TeamSkillOwnerAction, versionId?: string) => {
    if (!history) return;
    const version = versionId ? history.versions.find((item) => item.versionId === versionId) : null;
    const safeDisplayName = safeConfirmationLabel(history.skill.displayName);
    const binding = action === "approve"
      ? `${safeDisplayName} version ${history.skill.pendingApproval?.versionNumber}`
      : action === "restore"
        ? `${safeDisplayName} version ${version?.versionNumber}`
        : `${action} ${safeDisplayName}`;
    returnFocus.current = document.activeElement as HTMLElement | null;
    setConfirmation({ action, skill: history.skill, ...(versionId ? { versionId } : {}), binding });
    setConfirmationText("");
  };

  const closeConfirmation = () => {
    setConfirmation(null); setConfirmationText("");
    queueMicrotask(() => returnFocus.current?.focus());
  };

  const confirmOwnerAction = async () => {
    if (!confirmation || confirmationText !== confirmation.binding) return;
    const approvalId = confirmation.action === "approve" ? confirmation.skill.pendingApproval?.approvalId : undefined;
    setPending(true); setError(null);
    try {
      const result = await api.beginTeamSkillOwnerStepUp(workspaceId, confirmation.skill.skillId, {
        action: confirmation.action,
        ...(approvalId ? { approvalId } : {}),
        ...(confirmation.versionId ? { versionId: confirmation.versionId } : {}),
        expectedStateGeneration: confirmation.skill.stateGeneration,
      });
      const expectedBindingVersionId = confirmation.versionId ?? confirmation.skill.liveVersion?.versionId ?? null;
      if (result.binding.workspaceId !== workspaceId || result.binding.skillId !== confirmation.skill.skillId
        || result.binding.action !== confirmation.action || result.binding.versionId !== expectedBindingVersionId
        || result.binding.expectedStateGeneration !== confirmation.skill.stateGeneration) {
        throw new Error("Doorway could not bind verification to the exact displayed change.");
      }
      sessionStorage.setItem(PENDING_OWNER_ACTION_KEY, JSON.stringify({
        skillId: confirmation.skill.skillId, action: confirmation.action,
        ...(approvalId ? { approvalId } : {}), ...(confirmation.versionId ? { versionId: confirmation.versionId } : {}),
        bindingVersionId: result.binding.versionId,
        expectedStateGeneration: confirmation.skill.stateGeneration,
      }));
      window.location.assign(result.authorizationUrl);
    } catch (cause) {
      setPending(false); handleMutationError(cause);
    }
  };

  const visible = (library?.skills ?? []).filter((skill) => {
    const query = search.trim().toLocaleLowerCase();
    const matchesSearch = !query || [
      skill.displayName,
      skill.canonicalName,
      skill.description,
      skill.liveVersion?.lastUpdatedBy.name,
      skill.latestSharedVersion?.contributor.name,
      ...(skill.collectionAreas ?? []),
      ...(skill.tags ?? []),
    ].some((value) => value?.toLocaleLowerCase().includes(query));
    const matchesTags = selectedTags.length === 0
      || selectedTags.some((tag) => (skill.tags ?? []).includes(tag));
    return matchesSearch && matchesTags;
  });
  const collections = visible.filter((skill) => skill.presentationKind === "collection");
  const individualSkills = visible.filter((skill) => skill.presentationKind !== "collection");
  const workspaceTags = [...new Set([
    ...(library?.tags ?? []),
    ...(library?.skills ?? []).flatMap((skill) => skill.tags ?? []),
  ])].sort((left, right) => left.localeCompare(right, "en", { sensitivity: "base" }));
  const visibleTagOptions = workspaceTags.filter((tag) =>
    tag.toLocaleLowerCase().includes(tagSearch.trim().toLocaleLowerCase()),
  );
  const effectiveRole = library?.role ?? role;
  const canPublish = effectiveRole === "creator" || effectiveRole === "owner";
  const canAdminister = effectiveRole === "owner";

  return (
    <section className="workspace-section team-skills-section" aria-label="Team Skills">
      <div className="workspace-section-heading">
        <div><p className="step-label">Team library</p><h2>Skills</h2></div>
        <div className="team-skills-heading-actions">
          {canPublish && (
            <button ref={uploadButton} className="primary-button" type="button" onClick={() => {
              setUploadError(null);
              setUploadOpen(true);
            }}>
              Upload skill
            </button>
          )}
          <div className="team-skills-download">
            <a className="secondary-button" href={DOORWAY_SYNC_DOWNLOAD_URL}>Get Doorway Sync for Mac</a>
            <small>Requires macOS 14.4 or later</small>
          </div>
          {library && <span className="team-skills-cursor">Audit cursor {library.auditCursor}</span>}
        </div>
      </div>
      <p className="workspace-section-copy">Approve your skills folder once in Doorway Sync. New skills and content updates share automatically, while Private skills and new capabilities stay under your control.</p>
      {notice && <p className="workspace-notice" role="status">{notice}</p>}
      {error && <div className="flow-error" role="alert"><p>{error}</p><button className="secondary-button" type="button" onClick={() => void load()}>Try again</button></div>}

      {library && library.skills.length > 0 && <LibraryCoverage skills={library.skills} />}

      <label className="workspace-search">
        <span>Search skills</span>
        <input type="search" value={search} placeholder="Search by skill or teammate" onChange={(event) => setSearch(event.target.value)} />
      </label>
      {workspaceTags.length > 0 && (
        <div className="team-skill-tag-filter" aria-label="Filter by tags">
          <div>
            <strong>Filter by tag</strong>
            {selectedTags.length > 0 && (
              <button className="text-button" type="button" onClick={() => setSelectedTags([])}>Clear</button>
            )}
          </div>
          {workspaceTags.length > 8 && (
            <input
              type="search"
              value={tagSearch}
              placeholder="Find a tag"
              aria-label="Find a tag"
              onChange={(event) => setTagSearch(event.target.value)}
            />
          )}
          <div className="team-skill-tag-list">
            {visibleTagOptions.map((tag) => (
              <button
                key={tag}
                type="button"
                aria-pressed={selectedTags.includes(tag)}
                onClick={() => setSelectedTags((current) =>
                  current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag])}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}

      {!library && !error ? <p className="workspace-empty">Loading your team library…</p> : library?.skills.length === 0 ? (
        <div className="team-skills-empty"><strong>Your team library is ready.</strong><span>Open Doorway Sync and approve your skills folder once. Valid skills will appear here automatically.</span></div>
      ) : (
        <>
          {collections.length > 0 && (
            <section className="team-skill-collections" aria-labelledby="team-skill-collections-title">
              <div>
                <p className="step-label">Grouped context</p>
                <h3 id="team-skill-collections-title">Skill collections</h3>
              </div>
              <div className="team-skill-collection-grid">
                {collections.map((skill) => (
                  <button
                    key={skill.skillId}
                    type="button"
                    aria-pressed={selectedSkillId === skill.skillId}
                    onClick={() => void selectSkill(skill.skillId)}
                  >
                    <span className="team-skill-collection-icon" aria-hidden="true">✦</span>
                    <span>
                      <strong>{skill.displayName}</strong>
                      <small>{skill.description || "A connected set of skills and reference areas."}</small>
                      <span className="team-skill-collection-meta">
                        {(skill.collectionAreas ?? []).length} areas
                        {(skill.collectionAreas ?? []).length > 0
                          ? ` · ${(skill.collectionAreas ?? []).slice(0, 3).join(", ")}`
                          : ""}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}
          <div className="team-skills-layout">
          <div className="team-skill-list" role="list" aria-label="Team skills">
            <div className="team-skill-list-heading">
              <strong>Individual skills</strong>
              <span>{individualSkills.length}</span>
            </div>
            {visible.length === 0 ? <p className="workspace-empty">No skills match those filters.</p> : individualSkills.length === 0 ? (
              <p className="workspace-empty">No individual skills match those filters.</p>
            ) : individualSkills.map((skill) => (
              <div key={skill.skillId} role="listitem">
                <button type="button" aria-pressed={selectedSkillId === skill.skillId} onClick={() => void selectSkill(skill.skillId)}>
                  <span>
                    <strong>{skill.displayName}</strong>
                    <small>
                      {skill.latestSharedVersion
                        ? `Version ${skill.latestSharedVersion.versionNumber} shared by ${skill.latestSharedVersion.contributor.name}`
                        : skill.liveVersion
                          ? `Version ${skill.liveVersion.versionNumber} · Last updated by ${skill.liveVersion.lastUpdatedBy.name}${skill.pendingDrafts > 0 ? ` · ${skill.pendingDrafts} pending draft${skill.pendingDrafts === 1 ? "" : "s"}` : ""}`
                          : `Waiting for its first shared version${skill.pendingDrafts > 0 ? ` · ${skill.pendingDrafts} pending draft${skill.pendingDrafts === 1 ? "" : "s"}` : ""}`}
                    </small>
                  </span>
                  <span className={`team-skill-health${skill.health.failedDevices > 0 ? " team-skill-health-warning" : ""}`}>
                    {skill.lifecycle === "removed"
                      ? "Removed"
                      : skill.lifecycle === "disabled"
                        ? "Disabled"
                        : skill.latestSharedVersion
                            ? "Ready to publish"
                            : healthLabel(skill)}
                  </span>
                </button>
              </div>
            ))}
          </div>
          <div className="team-skill-detail">
            {history && history.skill.skillId === selectedSkillId ? (
              <>
                <div className="team-skill-tags-editor">
                  <div>
                    <strong>Tags</strong>
                    <small>Shared with everyone in this workspace</small>
                  </div>
                  <div className="team-skill-tag-list">
                    {workspaceTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        disabled={!canPublish || pending}
                        aria-pressed={(history.skill.tags ?? []).includes(tag)}
                        onClick={() => {
                          const current = history.skill.tags ?? [];
                          void saveTags(
                            history.skill,
                            current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag],
                          );
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  {canPublish && (
                    <form onSubmit={(event) => {
                      event.preventDefault();
                      const tag = newTag.trim().replace(/\s+/g, " ");
                      if (!tag || (history.skill.tags ?? []).includes(tag)) return;
                      void saveTags(history.skill, [...(history.skill.tags ?? []), tag]);
                    }}>
                      <input
                        value={newTag}
                        maxLength={32}
                        placeholder="Create a tag"
                        aria-label="Create a tag"
                        onChange={(event) => setNewTag(event.target.value)}
                      />
                      <button className="secondary-button" type="submit" disabled={pending || !newTag.trim()}>Add</button>
                    </form>
                  )}
                </div>
                <TeamSkillHistory key={history.skill.skillId} history={history} canPublish={canPublish} canAdminister={canAdminister}
                  pending={pending} onPublish={(versionId) => void publish(versionId)} onOwnerAction={requestOwnerAction}
                  onCompare={(versionId, baseVersionId) => api.getTeamSkillVersionComparison(
                    workspaceId,
                    history.skill.skillId,
                    versionId,
                    baseVersionId,
                  )}
                  loadingMore={loadingMoreHistory} onLoadMore={() => void loadMoreHistory()} />
                {canAdminister && history.skill.lifecycle !== "removed" && (
                  <div className="team-skill-admin-actions" aria-label="Owner controls">
                    {history.skill.lifecycle === "active" && <button className="secondary-button" type="button" disabled={pending} onClick={() => void directOwnerAction(history.skill.locked ? "unlock" : "lock")}>{history.skill.locked ? "Unlock updates" : "Lock updates"}</button>}
                    {history.skill.lifecycle === "active" && <button className="text-button danger-text" type="button" disabled={pending} onClick={() => void directOwnerAction("disable")}>Disable skill</button>}
                    <button className="text-button danger-text" type="button" disabled={pending} onClick={() => requestOwnerAction("remove")}>Remove from team</button>
                  </div>
                )}
              </>
            ) : selectedSkillId ? <p className="workspace-empty">Loading skill history…</p> : null}
          </div>
        </div>
        </>
      )}

      {confirmation && (
        <div className="team-skill-dialog-backdrop">
          <section
            ref={confirmationDialog}
            className="team-skill-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-skill-confirm-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") closeConfirmation();
              if (event.key === "Tab") trapDialogFocus(event, confirmationDialog.current);
            }}
          >
            <p className="step-label">Owner confirmation</p>
            <h3 id="team-skill-confirm-title">Confirm this exact change</h3>
            <p>Doorway will ask you to sign in again. Type <strong><bdi>{confirmation.binding}</bdi></strong> to continue.</p>
            <label>Exact change<input ref={confirmationInput} value={confirmationText} onChange={(event) => setConfirmationText(event.target.value)} /></label>
            <div><button className="text-button" type="button" onClick={closeConfirmation}>Cancel</button><button className="primary-button" type="button" disabled={pending || confirmationText !== confirmation.binding} onClick={() => void confirmOwnerAction()}>Verify and continue</button></div>
          </section>
        </div>
      )}

      {uploadOpen && (
        <div className="team-skill-dialog-backdrop">
          <section
            ref={uploadDialog}
            className="team-skill-dialog team-skill-upload-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team-skill-upload-title"
            tabIndex={-1}
            onKeyDown={(event) => {
              if (event.key === "Tab") {
                trapDialogFocus(event, uploadDialog.current);
                return;
              }
              if (event.key !== "Escape" || uploading) return;
              setUploadOpen(false);
              setUploadFiles([]);
              setUploadError(null);
              queueMicrotask(() => uploadButton.current?.focus());
            }}
          >
            <p className="step-label">Add to Team Skills</p>
            <h3 id="team-skill-upload-title">Upload a skill folder</h3>
            <p>Choose one folder with a root SKILL.md. Doorway checks the same file, secret, and capability rules used by Doorway Sync.</p>
            <FolderPicker
              variant="skill"
              {...(uploadFiles.length > 0 ? {
                selection: {
                  title: `${uploadFiles.length} file${uploadFiles.length === 1 ? "" : "s"} ready`,
                  description: uploadFiles.some((file) => (file.webkitRelativePath || file.name).endsWith("/SKILL.md"))
                    ? "SKILL.md found. Share the exact folder when you are ready."
                    : "This folder needs a root SKILL.md.",
                },
              } : {})}
              onChoose={(files) => {
                setUploadError(null);
                setUploadFiles(Array.from(files));
              }}
            />
            {uploadError && <p className="flow-error" role="alert">{uploadError}</p>}
            <div className="team-skill-dialog-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={uploading}
                onClick={() => {
                  setUploadOpen(false);
                  setUploadFiles([]);
                  setUploadError(null);
                  queueMicrotask(() => uploadButton.current?.focus());
                }}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                type="button"
                disabled={uploading || uploadFiles.length === 0}
                onClick={() => void shareUploadedSkill()}
              >
                {uploading ? "Checking and sharing…" : "Share with team"}
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}

function LibraryCoverage({ skills }: { skills: TeamSkillLibraryItem[] }) {
  const active = skills.filter((skill) => skill.lifecycle === "active");
  const expectedInstalls = active.reduce((total, skill) => total + skill.health.managedDevices, 0);
  const currentInstalls = active.reduce((total, skill) => total + skill.health.currentDevices, 0);
  const failures = active.reduce((total, skill) => total + skill.health.failedDevices, 0);
  const readyToPublish = skills.filter((skill) => skill.latestSharedVersion).length;
  return (
    <dl className="team-skills-coverage" aria-label="Team Skills update coverage">
      <div><dt>Team Live</dt><dd>{active.filter((skill) => skill.liveVersion).length} / {active.length}</dd></div>
      <div><dt>Device updates</dt><dd>{expectedInstalls === 0 ? "No devices" : `${currentInstalls} / ${expectedInstalls}`}</dd></div>
      <div><dt>Ready to publish</dt><dd>{readyToPublish}</dd></div>
      <div><dt>Failed updates</dt><dd>{failures}</dd></div>
    </dl>
  );
}

function healthLabel(skill: TeamSkillLibraryItem): string {
  if (skill.health.managedDevices === 0) return "No devices yet";
  if (skill.health.failedDevices > 0) return `${skill.health.failedDevices} need help`;
  if (skill.health.pausedDevices > 0) return `${skill.health.pausedDevices} paused`;
  if (skill.health.currentDevices === skill.health.managedDevices) return "Up to date";
  return `${skill.health.currentDevices}/${skill.health.managedDevices} updated`;
}

function message(cause: unknown, fallback: string): string {
  return cause instanceof Error ? cause.message : fallback;
}

function uploadMessage(cause: unknown): string {
  if (!(cause instanceof AccountRequestError)) return message(cause, "Doorway could not upload that skill folder.");
  if (cause.code === "SKILL_CONFLICT") return "A Team Skill with this name already exists.";
  if (cause.code === "UPLOAD_SAFE_ENVELOPE_EXCEEDED") return "Choose a skill with no more than 100 files and 16 MiB.";
  if (cause.status === 422) return "Doorway found a file, secret, or skill metadata issue in this folder.";
  if (cause.status === 403) return "Your Team Skill publishing access changed. Refresh the workspace and try again.";
  return "Doorway could not upload that skill folder. Try again.";
}

function safeConfirmationLabel(value: string): string {
  return value.replace(/[\p{Cc}\p{Cf}]/gu, "�");
}

function readStoredRecord(key: string): Record<string, unknown> | null {
  const value = sessionStorage.getItem(key);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed as Record<string, unknown> : null;
  } catch { return null; }
}

function trapDialogFocus(event: React.KeyboardEvent, dialog: HTMLElement | null): void {
  if (!dialog) return;
  const focusable = [...dialog.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled), [href]")];
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault(); last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault(); first.focus();
  }
}
